<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { ToggleProps } from "./Toggle.props"

const props = withDefaults(defineProps<ToggleProps>(), {
  label: "",
  modelValue: false,
  isDisabled: false
})

const emit =
  defineEmits<{
    (e: "update:modelValue", value: boolean): void
    (e: "clicked"): void
  }>()

const value = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    if (!value) value = false
    emit("update:modelValue", value)
  }
})

const isHover = ref(false)
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormKit
    type="checkbox"
    v-model="value"
    :name="name"
    :label="label"
    @click="emit('clicked')"
    :classes="{
      outer: 'inline-flex',
      wrapper: 'flex items-center gap-x-2',
      inner: {
        'relative flex items-center rounded-full transition-background-color duration-300 cursor-pointer': true
      },
      label: {
        'text-grey-200 select-none': isDisabled
      },
      input: 'opacity-0 h-0 w-0'
    }"
    :disabled="isDisabled"
  >
    <template #decorator>
      <div
        tabindex="0"
        @mouseover="isHover = true"
        @mouseleave="isHover = false"
        class="
          decorator-container
          flex
          h-4
          w-7
          items-center
          rounded-full
          border-2
          focus:outline focus:outline-1 focus:outline-offset-2
          focus:ring-2 focus:ring-green-10
        "
        :class="{
          'active justify-end border-green-main bg-green-main pr-[1px]': value,

          'justify-start border-black-40 pl-[1px] hover:border-green-70 focus:border-black-80':
            !value,

          'hover:border-green-300 hover:bg-green-300 focus:outline-green-10':
            !isDisabled && value,

          'focus:outline-green-10': !isDisabled && !value,

          'disabled cursor-not-allowed bg-white hover:border-black-40 focus:border-black-40 focus:outline-none focus:outline-offset-0 focus:ring-0':
            isDisabled && !value,

          'cursor-not-allowed border-grey-200 bg-grey-200 focus:border-grey-200 focus:outline-none focus:outline-offset-0 focus:ring-0':
            isDisabled && value
        }"
      >
        <IconsToggleChecker
          class="toggle-check text-black-40"
          :class="[
            { 'text-white focus:text-white': value },
            isHover && !value && !isDisabled ? 'text-green-70' : 'text-black-40'
          ]"
        />
      </div>
    </template>
  </FormKit>
</FormKitLazyProvider>

</template>

<style lang="scss" scoped>
.decorator-container {
  &:focus {
    .toggle-check {
      @apply text-black-80;
    }
  }

  &.active {
    .toggle-check {
      @apply text-white;
    }
  }

  &.disabled {
    .toggle-check {
      @apply text-black-40;
    }
  }
}
</style>
